// UX-003: which properties and figures the MTD Quarterly Reports page uses.
// Plain CommonJS on purpose, so the backend Jest suite (the "Tests" check) can test it directly,
// and Reports.js can import it as usual.

const { isIrishProperty } = require('./propertyCountry');
const { isCapitalCategory } = require('./expenseCategories');
const { propertyShare, isJointProperty, shareOf, roundPence, signedMoney, shareLabel, JOINT_FILING_NOTE, SHARE_PROMPT } = require('./jointShare');

// FEAT-002: the page totals are for personally owned UK properties; jointly owned ones are listed separately below,
// at 100% (the old "joint properties are not included yet" note is gone).
const MTD_SCOPE_TEXT = 'UK personally owned properties (totals). Jointly owned properties are listed separately below. Company-owned and Irish properties are excluded.';

// A property counts for MTD when it is: not archived, personally owned (NOT company, NOT joint),
// and not in the Republic of Ireland. Northern Ireland is part of the UK, so it stays in.
// A missing ownership means personal and a missing jurisdiction means England (the app's defaults).
function isMtdProperty(p) {
  if (!p || p.archived) return false;
  const ownership = String(p.ownership || 'personal').toLowerCase();
  if (ownership !== 'personal') return false;
  return !isIrishProperty(p);   // BUG-005a: Irish = jurisdiction Ireland OR currency EUR
}

function mtdProperties(properties) {
  return (properties || []).filter(isMtdProperty);
}

// '2025-2026' -> the four HMRC quarters (6 April to 5 April)
function mtdQuarterRanges(taxYear) {
  const [startYear, endYear] = String(taxYear).split('-').map(Number);
  return {
    q1: { start: new Date(startYear, 3, 6), end: new Date(startYear, 6, 5), label: 'Q1 (Apr 6 - Jul 5)' },
    q2: { start: new Date(startYear, 6, 6), end: new Date(startYear, 9, 5), label: 'Q2 (Jul 6 - Oct 5)' },
    q3: { start: new Date(startYear, 9, 6), end: new Date(endYear, 0, 5), label: 'Q3 (Oct 6 - Jan 5)' },
    q4: { start: new Date(endYear, 0, 6), end: new Date(endYear, 3, 5), label: 'Q4 (Jan 6 - Apr 5)' },
  };
}

const propId = (x) => x.propertyId || x.property_id;
const isPaid = (e) => e.paid === true || e.paid === 'true';
const inRange = (d, start, end) => d >= start && d <= end;

// Rent payments for the properties, counted by the middle of their month (as the table always has)
function mtdIncomeItems(properties, payments, start, end) {
  return (payments || []).filter((p) => {
    if (!properties.some((fp) => String(fp.id) === String(propId(p)))) return false;
    if (!p.month) return false;
    const [y, m] = p.month.split('-').map(Number);
    return inRange(new Date(y, m - 1, 15), start, end);
  });
}

// Paid expenses for the properties. FEAT-006: capital costs (improvements, purchase and sale costs, initial furnishing)
// are not allowable expenses, so they are never part of the MTD figures, the detail lines or the Excel.
function mtdExpenseItems(properties, expenses, start, end) {
  return (expenses || []).filter((e) =>
    properties.some((fp) => String(fp.id) === String(propId(e))) && isPaid(e) && !isCapitalCategory(e.category) && e.date &&
    inRange(new Date(e.date), start, end));
}

const sum = (items) => items.reduce((s, x) => s + parseFloat(x.amount || 0), 0);

// One row per selected quarter: { quarter, income, expenses, profit }
function mtdQuarterData(properties, payments, expenses, taxYear, quarter) {
  const ranges = mtdQuarterRanges(taxYear);
  const mtdProps = mtdProperties(properties);
  const selected = quarter === 'all' || !quarter ? ['q1', 'q2', 'q3', 'q4'] : [quarter];
  return selected.map((q) => {
    const { start, end, label } = ranges[q];
    const income = sum(mtdIncomeItems(mtdProps, payments, start, end));
    const exp = sum(mtdExpenseItems(mtdProps, expenses, start, end));
    return { quarter: label, income, expenses: exp, profit: income - exp };
  });
}

// The rows behind the Excel "Income Detail" and "Expense Detail" sheets (whole selected period)
function mtdDetailItems(properties, payments, expenses, taxYear, quarter) {
  const ranges = mtdQuarterRanges(taxYear);
  const selected = quarter === 'all' || !quarter ? ['q1', 'q2', 'q3', 'q4'] : [quarter];
  const start = selected.map((q) => ranges[q].start).reduce((a, d) => (d < a ? d : a));
  const end = selected.map((q) => ranges[q].end).reduce((a, d) => (d > a ? d : a));
  const mtdProps = mtdProperties(properties);
  return {
    properties: mtdProps,
    income: mtdIncomeItems(mtdProps, payments, start, end),
    expenses: mtdExpenseItems(mtdProps, expenses, start, end),
  };
}

// ---- FEAT-002: jointly owned UK properties, shown separately at 100% -------------------------------------------------
// UK, jointly owned, not archived. (Company-owned and Irish properties are never included.)
function isMtdJointProperty(p) {
  if (!p || p.archived || !isJointProperty(p)) return false;
  return !isIrishProperty(p);
}
const mtdJointProperties = (properties) => (properties || []).filter(isMtdJointProperty);

// One entry per joint property: its quarters at 100%, its ownership share, and "your share" for information only.
// Nothing here is ever added to the personal figures from mtdQuarterData.
function mtdJointQuarterData(properties, payments, expenses, taxYear, quarter) {
  const ranges = mtdQuarterRanges(taxYear);
  const selected = quarter === 'all' || !quarter ? ['q1', 'q2', 'q3', 'q4'] : [quarter];
  return mtdJointProperties(properties).map((prop) => {
    const sharePercent = propertyShare(prop);
    const rows = selected.map((q) => {
      const { start, end, label } = ranges[q];
      const income = sum(mtdIncomeItems([prop], payments, start, end));
      const exp = sum(mtdExpenseItems([prop], expenses, start, end));
      const profit = income - exp;
      return {
        quarter: label, income, expenses: exp, profit,
        yourIncome: shareOf(income, sharePercent), yourExpenses: shareOf(exp, sharePercent), yourProfit: shareOf(profit, sharePercent),
      };
    });
    const totalOf = (f) => rows.reduce((s, r) => s + f(r), 0);
    const yourOf = (f) => (sharePercent === null ? null : roundPence(rows.reduce((s, r) => s + f(r), 0)));
    return {
      id: prop.id, address: prop.address, currency: prop.currency || 'GBP', sharePercent, shareMissing: sharePercent === null, rows,
      totals: {
        income: totalOf((r) => r.income), expenses: totalOf((r) => r.expenses), profit: totalOf((r) => r.profit),
        yourIncome: yourOf((r) => r.yourIncome), yourExpenses: yourOf((r) => r.yourExpenses), yourProfit: yourOf((r) => r.yourProfit),
      },
    };
  });
}

// the rows behind the Excel "Joint Income Detail" / "Joint Expense Detail" sheets (same dates and rules as the table)
function mtdJointDetailItems(properties, payments, expenses, taxYear, quarter) {
  const ranges = mtdQuarterRanges(taxYear);
  const selected = quarter === 'all' || !quarter ? ['q1', 'q2', 'q3', 'q4'] : [quarter];
  const start = selected.map((q) => ranges[q].start).reduce((a, d) => (d < a ? d : a));
  const end = selected.map((q) => ranges[q].end).reduce((a, d) => (d > a ? d : a));
  const jointProps = mtdJointProperties(properties);
  return {
    properties: jointProps,
    income: mtdIncomeItems(jointProps, payments, start, end),
    expenses: mtdExpenseItems(jointProps, expenses, start, end),
  };
}

// The joint block of the MTD Excel summary sheet (9 columns). The screen shows the same entries.
function mtdJointExcelRows(entries, fmt = signedMoney) {
  const blank = (n) => Array(n).fill('');
  if (!entries || !entries.length) return [];
  const rows = [
    ['JOINTLY OWNED UK PROPERTIES — 100% FIGURES', ...blank(8)],
    [JOINT_FILING_NOTE, ...blank(8)],
    ['Property', 'Quarter', 'Income (100%)', 'Expenses (100%)', 'Net Profit (100%)', 'Ownership share %',
      'Your share: Income (info only)', 'Your share: Expenses (info only)', 'Your share: Net Profit (info only)'],
  ];
  for (const e of entries) {
    const mine = (v) => (e.shareMissing ? '' : fmt(v));
    e.rows.forEach((r, i) => rows.push([
      e.address, r.quarter, fmt(r.income), fmt(r.expenses), fmt(r.profit), shareLabel(e.sharePercent),
      e.shareMissing ? (i === 0 ? SHARE_PROMPT : '') : fmt(r.yourIncome), mine(r.yourExpenses), mine(r.yourProfit),
    ]));
    rows.push([e.address, 'Total (100%)', fmt(e.totals.income), fmt(e.totals.expenses), fmt(e.totals.profit), shareLabel(e.sharePercent),
      e.shareMissing ? '' : fmt(e.totals.yourIncome), mine(e.totals.yourExpenses), mine(e.totals.yourProfit)]);
  }
  return rows;
}

module.exports = {
  MTD_SCOPE_TEXT, JOINT_FILING_NOTE, mtdJointExcelRows,
  isMtdProperty, mtdProperties, mtdQuarterRanges, mtdQuarterData, mtdDetailItems,
  isMtdJointProperty, mtdJointProperties, mtdJointQuarterData, mtdJointDetailItems,
};
